import { useState } from 'react';
import { NOVEL_GENRES, NOVEL_PLAY_TIMES, NOVEL_SETTINGS, NOVEL_TONES } from './options';
import { generateNovelGame, NovelApiError } from './api';
import { useNovelStore } from './novelStore';

function OptionGrid<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly { id: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <section className="space-y-2">
      <p className="field-label !mb-0">{label}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {options.map((o) => {
          const on = value === o.id;
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onChange(o.id)}
              className={`rounded-xl border px-3 py-2.5 text-left text-sm touch-manipulation ${
                on
                  ? 'border-accent/50 bg-accent/10 text-accent'
                  : 'border-base-600 bg-base-900 text-slate-300 hover:border-base-500'
              }`}
              aria-pressed={on}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </section>
  );
}

export function AiNovelSetupScreen() {
  const draft = useNovelStore((s) => s.draft);
  const setDraft = useNovelStore((s) => s.setDraft);
  const backToHub = useNovelStore((s) => s.backToHub);
  const startPlay = useNovelStore((s) => s.startPlay);
  const setError = useNovelStore((s) => s.setError);
  const [busy, setBusy] = useState(false);

  const generate = () => {
    if (busy) return;
    setBusy(true);
    void (async () => {
      try {
        const r = await generateNovelGame(draft);
        startPlay(r.game, r.source);
      } catch (e) {
        setError(e instanceof NovelApiError ? e.message : 'ゲームの生成に失敗しました。しばらくしてからもう一度お試しください。');
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div className="library-shell flex h-full flex-col">
      <header className="pt-safe-header shrink-0 border-b border-base-600/60 bg-[#07090f] px-4 pb-3">
        <div className="flex items-center gap-3">
          <button type="button" className="btn-ghost !min-h-0 !px-3 !py-1.5 !text-xs" onClick={backToHub}>
            ← Games
          </button>
          <h1 className="text-base font-semibold">AI Novel</h1>
        </div>
      </header>

      <div className="mx-auto min-h-0 w-full max-w-xl flex-1 space-y-6 overflow-y-auto px-4 pb-[calc(5.5rem+env(safe-area-inset-bottom,0px))] pt-4 md:pb-10 landscape:pb-10">
        <p className="text-sm leading-relaxed text-slate-400">
          ジャンル・トーン・舞台を選んでストーリーを生成します。生成は1回だけ行い、あとは端末内でプレイします。
        </p>

        <OptionGrid
          label="Genre"
          options={NOVEL_GENRES}
          value={draft.genre as (typeof NOVEL_GENRES)[number]['id']}
          onChange={(genre) => setDraft({ genre })}
        />
        <OptionGrid
          label="Tone"
          options={NOVEL_TONES}
          value={draft.tone as (typeof NOVEL_TONES)[number]['id']}
          onChange={(tone) => setDraft({ tone })}
        />
        <OptionGrid
          label="Setting"
          options={NOVEL_SETTINGS}
          value={draft.setting as (typeof NOVEL_SETTINGS)[number]['id']}
          onChange={(setting) => setDraft({ setting })}
        />
        <OptionGrid
          label="Play Time"
          options={NOVEL_PLAY_TIMES}
          value={draft.playTime}
          onChange={(playTime) => setDraft({ playTime })}
        />

        <button type="button" className="btn-primary w-full" disabled={busy} onClick={generate}>
          {busy ? '生成中…' : 'Generate Story'}
        </button>
      </div>

      {busy && (
        <div className="pointer-events-none fixed inset-0 z-[55] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="card pointer-events-none mx-4 max-w-sm p-5 text-center">
            <p className="font-display text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-accent">
              Generating
            </p>
            <p className="mt-2 text-sm text-slate-200">物語を生成しています…</p>
          </div>
        </div>
      )}
    </div>
  );
}
